import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronDown, Layers, Cpu, Zap, Shield, Globe,
  FileText, ArrowRight, Settings, Pipette, Ruler, Building2,
  HardDrive, Cable, Flame, Box, AlertTriangle, Wrench, CircuitBoard,
  Database, CheckCircle, Code, Play, RotateCcw
} from "lucide-react";
import { CtaForm } from "../components/CtaForm";
import RachanaPnidCanvas from "../components/RachanaPnidCanvas";
import RachanaLogo from "../components/RachanaLogo";

/* ───── CountUp ───── */
function CountUp({ end, prefix = "", suffix = "", duration = 1600, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / duration);
          setVal(end * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const d = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{prefix}{d}{suffix}</span>;
}

/* ───── FaqItem ───── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`grantha-faq-item${open ? " open" : ""}`} onClick={() => setOpen(!open)}>
      <div className="grantha-faq-q"><span>{q}</span><ChevronDown size={18} className={`grantha-faq-icon${open ? " rot" : ""}`} /></div>
      {open && <div className="grantha-faq-a">{a}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ARCHITECTURE LAYERS
   ═══════════════════════════════════════════════════════════════ */
const LAYERS = [
  { n: "01", name: "Intent & Process Understanding", sub: "Natural language in. Structured process schema out.", icon: Cpu,
    body: "Interprets the prompt, identifies process type across 47 categories, extracts design parameters, applies 850+ engineering heuristics as intelligent defaults, generates a structured Process Design Object (PDO). Clarification protocol asks focused questions for safety-critical ambiguities \u2014 never guesses.",
    caps: ["47 process type classification", "Entity extraction (capacity, purity, pressure, temperature)", "850+ engineering heuristic defaults", "Multi-turn refinement on design iterations"] },
  { n: "02", name: "PFD Generation", sub: "ISO 10628 compliant. Auto-laid out. Stream-table linked.", icon: Layers,
    body: "Transforms structured process data into a professional PFD with equipment symbols, stream lines, stream data table, and material balance. Auto-applies ISO 10628 drafting conventions \u2014 flow left-to-right, major equipment above centerline.",
    caps: ["180+ ISO 10628 symbol library", "Graph-layout engine (Dagre/ELK based)", "Stream table with mass/energy balance", "Export to SVG, DWG, DEXPI XML"] },
  { n: "03", name: "P&ID Generation", sub: "ISA 5.1 compliant. Control loops wired. 4,000 rules validated.", icon: CircuitBoard,
    body: "The hardest layer. Transforms the PFD into a full P&ID with every valve, instrument, control loop, safety device, line number, and tag correctly placed per ISA 5.1 / PIP PIC001.",
    caps: ["1,000+ P&ID symbol library (ISA 5.1)", "8 control loop types wired automatically", "SIS/SIF architecture per IEC 61511", "4,000+ validation rules across flow integrity, isolation, safety"] },
  { n: "04", name: "Multi-Discipline 3D Model", sub: "17 disciplines. IFC 4.3 / STEP / glTF. Fabrication-ready.", icon: Box,
    body: "Generates a complete multi-discipline 3D model \u2014 not just equipment, but piping, structural steel, civil foundations, electrical cable trays, HVAC ducts, instrumentation tubing, fire protection, and buildings. Every element parametric, every dimension traceable to code.",
    caps: ["60+ equipment types with full parametric 3D", "A* pipe routing with 28 support types", "Clash detection across 17 disciplines", "Auto-MTO and auto-isometric generation"] },
];

/* ═══════════════════════════════════════════════════════════════
   VALIDATION RULES
   ═══════════════════════════════════════════════════════════════ */
const RULES = [
  { name: "Flow integrity", count: 385 },
  { name: "Isolation & maintenance", count: 247 },
  { name: "Safety & relief", count: 412 },
  { name: "Instrument logic", count: 580 },
  { name: "Material compatibility", count: 820 },
  { name: "Clearance & access", count: 612 },
  { name: "HAZOP integrity", count: 1117 },
];

/* ═══════════════════════════════════════════════════════════════
   DISCIPLINES (for 3D section)
   ═══════════════════════════════════════════════════════════════ */
const DISCIPLINES = [
  { name: "Process Equipment", detail: "60+ parametric types", icon: Settings },
  { name: "Piping", detail: "All ASME sizes + specialties", icon: Pipette },
  { name: "Pipe Supports", detail: "28 support types", icon: Wrench },
  { name: "Structural Steel", detail: "Pipe racks, platforms, ladders", icon: Building2 },
  { name: "Civil & Foundations", detail: "12 foundation types", icon: Ruler },
  { name: "Electrical", detail: "Cable trays, conduits, MCCs", icon: Cable },
  { name: "Instrumentation", detail: "ISA 5.1 tagged devices", icon: CircuitBoard },
  { name: "HVAC", detail: "8 duct types + all fittings", icon: HardDrive },
  { name: "Fire Protection", detail: "NFPA 11/13/15/20 compliant", icon: Flame },
  { name: "Buildings", detail: "15 types with blast-rating", icon: Building2 },
  { name: "Underground", detail: "Trenches, duct banks, drainage", icon: Database },
  { name: "Clash Detection", detail: "17-discipline matrix", icon: AlertTriangle },
];

/* ═══════════════════════════════════════════════════════════════
   DELIVERABLES
   ═══════════════════════════════════════════════════════════════ */
const DELIVERABLES = [
  { name: "Process Flow Diagrams", format: "ISO 10628 \u00b7 SVG / DWG / DEXPI", detail: "Stream data tables \u00b7 Material balance" },
  { name: "P&ID Diagrams", format: "ISA 5.1 \u00b7 Every tag \u00b7 Every loop", detail: "PIP PIC001 \u00b7 engineering 3D suites compatible" },
  { name: "Multi-Discipline 3D Model", format: "IFC 4.3 \u00b7 STEP \u00b7 glTF \u00b7 DWG", detail: "17 disciplines \u00b7 Clash-validated" },
  { name: "Equipment Data Sheets", format: "API / ASME / TEMA formats", detail: "60+ equipment types \u00b7 Vendor-ready" },
  { name: "Line Designation Table", format: "Every line \u00b7 Fluid code \u00b7 Pipe class", detail: "Insulation \u00b7 Tracing \u00b7 P&ID reference" },
  { name: "Instrument Index & Loops", format: "Every tag \u00b7 Every loop \u00b7 Range", detail: "Signal type \u00b7 Control philosophy" },
  { name: "Material Take-Off", format: "Piping \u00b7 Structural \u00b7 Electrical", detail: "Spool-level breakdown \u00b7 BOMs" },
  { name: "Isometric Drawings", format: "ISO 6412 \u00b7 Auto-generated from 3D", detail: "Spool splits \u00b7 Weld maps" },
];

/* ═══════════════════════════════════════════════════════════════
   FAQS
   ═══════════════════════════════════════════════════════════════ */
const FAQS = [
  { q: "Does Formigen replace incumbent 3D plant-design suites?", a: "Not necessarily replace \u2014 augment. Formigen generates the initial design (PFD + P&ID + 3D model) that would traditionally take a 40-engineer team 12 weeks. Most enterprises then import Formigen's output into their existing CAD/3D tool. Exports to IFC 4.3, STEP AP242, DWG, DEXPI XML, and engineering 3D suites formats." },
  { q: "Is Formigen output truly fabrication-ready?", a: "For FEED-grade (Class 3, \u00b120%): yes. For EPC detailed engineering (Class 1, \u00b110%): Formigen output is the starting point \u2014 integrate vendor data sheets, finalize isometrics. Think of it as what a 12-week FEED produces, in a few hours." },
  { q: "How accurate is the 4,173-rule validation?", a: "Rules are traceable to specific clauses of ASME, API, ISA, IEC, NFPA, NEC, OSHA. False negatives benchmarked at <2% against human expert review. Compare with typical human review which misses 15-25% of issues." },
  { q: "What formats does Formigen export to?", a: "PFD/P&ID: SVG, DWG, DEXPI XML, engineering 3D suites, Visio. 3D Model: IFC 4.3, STEP AP242, glTF 2.0, DWG. Documents: PDF, DOCX. BOMs: XLSX, CSV. No proprietary lock-in." },
  { q: "Can Formigen handle cryogenic LNG, nuclear, or pharmaceutical?", a: "Yes, with caveats. Covers 47 process types including cryogenic LNG, nuclear BOP, pharmaceutical (ASME BPE). For novel processes, explicit confidence flags and clarification protocol for safety-critical ambiguities." },
  { q: "How does Formigen handle HAZOP?", a: "Systematic HAZOP pass: 9 guidewords applied to every stream with consequence scoring. Identifies risk scenarios, proposes safeguards, validates they're in the P&ID. This is design-review HAZOP, not a replacement for the formal study." },
  { q: "What's the typical engagement model?", a: "Three models: (1) Per-design \u2014 \u20b925K-2L per facility, (2) Subscription \u2014 \u20b918-50L/yr unlimited, (3) Enterprise \u2014 \u20b92-10Cr/yr with custom corpora and on-premise." },
  { q: "What's the typical generation time?", a: "Simple process (pump skid): 15-30 min. Medium (50 MW boiler): 2-4 hrs. Full unit (100 TPD ethanol): 3-6 hrs. Full FEED facility: 18-36 hrs. Traditional FEED: 8-16 weeks." },
  { q: "Does Formigen integrate with Aspen HYSYS?", a: "Yes. Imports material balance and thermodynamic data from HYSYS, PRO/II, UniSim, CHEMCAD, gPROMS. Uses simulation data to validate design assumptions." },
  { q: "How is data security handled?", a: "Three modes: shared Azure (ISO 27001), dedicated Azure with India data residency, or on-premise with self-hosted LLMs for classified/nuclear." },
  { q: "What if Formigen's output has an engineering error?", a: "Every output ships with citations, confidence scores, assumption flags, and a validation report. Final accountability rests with the chartered engineer. Transparent citation model ensures errors can be caught and corrected." },
];

/* ═══════ STANDARDS ═══════ */
const STANDARDS = [
  "ISA 5.1", "ISO 10628", "ASME BPVC VIII", "ASME B31.3", "ASME B16.5",
  "API 650", "API 620", "API 610", "API 521", "API 560",
  "IEC 61511", "IEC 61508", "IEC 60079", "NFPA 30", "NFPA 13",
  "NFPA 15", "NFPA 20", "NEC 70", "OSHA 1910", "TEMA",
  "NACE MR0175", "API RP 500", "AISC 360", "ACI 318", "IS 875",
  "IS 800", "PESO", "OISD", "PIP PIC001", "DEXPI",
];

/* ═══════ PAGE EXPORT ═══════ */
export default function PlatformRachana() {
  return (
    <>
      <SectionHero />
      <SectionProblem />
      <SectionArchitecture />
      <SectionStandards />
      <SectionValidation />
      <Section3DModel />
      <SectionDeliverables />
      <SectionComparison />
      <SectionFaq />
      <SectionCta />
    </>
  );
}

/* ═══════ S1 HERO ═══════ */
function SectionHero() {
  return (
    <section className="grantha-hero" data-testid="rc-hero">
      <div className="container grantha-hero-grid">
        <div className="grantha-hero-copy">
          <Link to="/platforms" className="detail-back" data-testid="rc-back"><ChevronLeft size={14} /> All platforms</Link>
          <div className="grantha-crumb mono">Platforms / Enterprise Digital / FORMIGEN</div>
          <div className="eyebrow" style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span>Generative AI Engineering & Design Engine</span>
          </div>
          <h1 className="grantha-h1" data-testid="rc-h1">
            <RachanaLogo />
            <span className="grantha-h1-en">From prompt to plant. <em>In hours, not years.</em></span>
          </h1>
          <p className="grantha-sub" data-testid="rc-hero-sub">
            Type: &ldquo;Design a 100 TPD ethanol distillation plant with heat integration.&rdquo; In three hours, Formigen returns a complete PFD, a fully-instrumented P&ID, a multi-discipline 3D model, and a 4,000-rule validation report. What used to take a 40-engineer team twelve weeks &mdash; done by one engineer in one afternoon.
          </p>
          <div className="grantha-ctas">
            <a href="https://formigen.capexara.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="rc-cta-access">Access Formigen <span className="arrow">&rarr;</span></a>
            <Link to="/contact?p=formigen" className="btn btn-ghost" data-testid="rc-cta-demo">See the engine live <span className="arrow">&rarr;</span></Link>
            <a href="#rc-architecture" className="btn btn-ghost">See the 4-layer architecture</a>
          </div>
          <div className="grantha-trust mono">
            ISA 5.1 &middot; ASME B31.3 &middot; API 650 &middot; NFPA 30 &middot; IEC 61511 &middot; NEC 70 &middot; 30+ engineering codes embedded
          </div>
        </div>
        <div className="grantha-hero-stage" data-testid="rc-hero-viz">
          <RachanaPnidCanvas />
        </div>
      </div>
    </section>
  );
}

/* Hero visualization: Prompt -> Reasoning -> Output (horizontal flow) */
function PromptToPlantViz() {
  return (
    <div className="rc-viz" data-testid="rc-hero-viz-inner">
      {/* Step 1: Prompt */}
      <div className="rc-viz-step">
        <div className="rc-viz-card rc-viz-prompt">
          <div className="rc-viz-card-label">PROMPT</div>
          <div className="rc-viz-card-body">
            <code>&gt; Design 100 TPD ethanol distillation plant with heat integration, ASME-compliant reboiler, 99.5% purity</code>
          </div>
          <div className="rc-viz-status">47 parameters parsed</div>
        </div>
      </div>

      <div className="rc-viz-arrow"><ArrowRight size={20} /></div>

      {/* Step 2: Reasoning Engine */}
      <div className="rc-viz-step">
        <div className="rc-viz-card rc-viz-engine">
          <div className="rc-viz-card-label">REASONING ENGINE</div>
          <div className="rc-viz-nodes">
            {["Process Synthesis", "Equipment Sizing", "Heat Integration", "Instrumentation", "Piping (B31.3)", "Safety (HAZOP)"].map((label, i) => (
              <div key={i} className="rc-viz-node">{label}</div>
            ))}
          </div>
          <div className="rc-viz-status" style={{ color: "#F26B1F" }}>4,173 rules validated</div>
        </div>
      </div>

      <div className="rc-viz-arrow"><ArrowRight size={20} /></div>

      {/* Step 3: Outputs */}
      <div className="rc-viz-step">
        <div className="rc-viz-card rc-viz-output">
          <div className="rc-viz-card-label">OUTPUT</div>
          <div className="rc-viz-outputs">
            <div className="rc-viz-out-item"><span className="rc-viz-out-tag">PFD</span><span className="rc-viz-out-spec">ISO 10628</span></div>
            <div className="rc-viz-out-item"><span className="rc-viz-out-tag">P&amp;ID</span><span className="rc-viz-out-spec">ISA 5.1 &middot; 847 tags</span></div>
            <div className="rc-viz-out-item"><span className="rc-viz-out-tag">3D MODEL</span><span className="rc-viz-out-spec">7 disciplines &middot; IFC 4.3</span></div>
          </div>
          <div className="rc-viz-status">Generated in 2h 47m</div>
        </div>
      </div>
    </div>
  );
}

/* ═══════ S2 PROBLEM ═══════ */
function SectionProblem() {
  const stats = [
    { n: 12, suffix: "-16 wk", label: "typical FEED duration for a mid-sized process plant" },
    { n: 40, suffix: "-60", label: "engineers required for a mid-sized FEED delivery" },
    { n: 8, prefix: "\u20b9", suffix: "-22 Cr", label: "typical FEED cost for a mid-sized process plant" },
    { n: 65, suffix: "%", label: "of engineering hours spent on drafting, not problem-solving" },
  ];
  return (
    <section className="grantha-problem" data-testid="rc-problem">
      <div className="container">
        <div className="eyebrow" style={{ color: "#5790C9" }}>THE ENGINEERING BOTTLENECK</div>
        <h2 className="grantha-problem-h reveal" data-testid="rc-problem-h2">
          A process-engineering team spends 70% of its time <em>drawing</em> &mdash; and only 30% actually <em>engineering.</em>
        </h2>
        <div className="grantha-problem-grid reveal">
          {stats.map((s, i) => (
            <div className="grantha-problem-tile" key={i} data-testid={`rc-stat-${i}`}>
              <div className="grantha-problem-num"><CountUp end={s.n} prefix={s.prefix || ""} suffix={s.suffix} /></div>
              <div className="grantha-problem-label">{s.label}</div>
            </div>
          ))}
        </div>
        <p className="grantha-problem-quote reveal">
          &ldquo;The software industry automated code generation a decade ago. Process engineering is next.&rdquo;
        </p>
      </div>
    </section>
  );
}

/* ═══════ S3 ARCHITECTURE (4 Layers) ═══════ */
function SectionArchitecture() {
  const [active, setActive] = useState(0);
  return (
    <section className="grantha-problem" id="rc-architecture" data-testid="rc-architecture">
      <div className="container">
        <div className="eyebrow" style={{ color: "#5790C9" }}>HOW THE ENGINE WORKS</div>
        <h2 className="grantha-problem-h reveal">Four layers. One engine. Every deliverable.</h2>
        <div className="rc-layers">
          {LAYERS.map((layer, i) => {
            const Icon = layer.icon;
            const isActive = active === i;
            return (
              <div key={i} className={`rc-layer${isActive ? " active" : ""}`} onClick={() => setActive(i)} data-testid={`rc-layer-${i}`}>
                <div className="rc-layer-head">
                  <span className="rc-layer-num">{layer.n}</span>
                  <div className="rc-layer-info">
                    <div className="rc-layer-name"><Icon size={20} /> {layer.name}</div>
                    <div className="rc-layer-sub">{layer.sub}</div>
                  </div>
                  <ChevronDown size={18} className={`grantha-faq-icon${isActive ? " rot" : ""}`} />
                </div>
                <div className={`rc-layer-body${isActive ? " show" : ""}`}>
                  <p>{layer.body}</p>
                  <div className="rc-layer-caps">
                    {layer.caps.map((c, j) => <div key={j} className="rc-cap"><CheckCircle size={14} /> {c}</div>)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mono" style={{ fontSize: 12, color: "var(--text-3)", marginTop: 24, textAlign: "center" }}>
          Every layer is individually addressable via API. Use Formigen as a full pipeline, or integrate a single layer into your existing toolchain.
        </p>
      </div>
    </section>
  );
}

/* ═══════ S4 STANDARDS ═══════ */
function SectionStandards() {
  return (
    <section className="grantha-problem" data-testid="rc-standards">
      <div className="container">
        <div className="eyebrow" style={{ color: "#5790C9" }}>CODE-COMPLIANT BY CONSTRUCTION</div>
        <h2 className="grantha-problem-h reveal">30+ engineering standards, embedded in the engine.</h2>
        <div className="rc-standards-grid reveal">
          {STANDARDS.map((s, i) => (
            <div key={i} className="rc-std-tile" style={{ animationDelay: `${i * 40}ms` }}>{s}</div>
          ))}
        </div>
        <p className="grantha-problem-quote reveal" style={{ maxWidth: 720 }}>
          Every output traceable to a specific clause of a specific standard. No more &ldquo;this passed because our senior engineer said so.&rdquo; Every decision &mdash; auditable, defensible, reproducible.
        </p>
      </div>
    </section>
  );
}

/* ═══════ S5 VALIDATION ENGINE ═══════ */
function SectionValidation() {
  return (
    <section className="grantha-problem" data-testid="rc-validation">
      <div className="container">
        <div className="eyebrow" style={{ color: "#5790C9" }}>4,173 RULES. ZERO AMBIGUITY.</div>
        <h2 className="grantha-problem-h reveal">The validation engine no human team could run manually.</h2>
        <p className="reveal" style={{ maxWidth: 680, marginBottom: 36, color: "var(--text-2)", lineHeight: 1.6 }}>
          Formigen runs 4,173 rules against every design. No human team can apply 4,000+ rules uniformly across 847 design variables. Formigen does it in minutes, every time.
        </p>
        <div className="rc-rules-grid reveal">
          {RULES.map((r, i) => (
            <div key={i} className="rc-rule" data-testid={`rc-rule-${i}`}>
              <div className="rc-rule-name">{r.name}</div>
              <div className="rc-rule-count"><CountUp end={r.count} /> / {r.count.toLocaleString()}</div>
              <div className="rc-rule-check"><CheckCircle size={16} /></div>
            </div>
          ))}
          <div className="rc-rule rc-rule-total">
            <div className="rc-rule-name">TOTAL</div>
            <div className="rc-rule-count"><CountUp end={4173} /> / 4,173</div>
            <div className="rc-rule-check" style={{ color: "#F26B1F" }}><CheckCircle size={18} /></div>
          </div>
        </div>
        <p className="mono reveal" style={{ fontSize: 12, color: "var(--text-3)", marginTop: 20, textAlign: "center" }}>
          Validation time: 47 seconds. Equivalent human review time: 400+ hours.
        </p>
      </div>
    </section>
  );
}

/* ═══════ S6 3D MODEL ═══════ */
function Section3DModel() {
  return (
    <section className="grantha-problem" data-testid="rc-3d">
      <div className="container">
        <div className="eyebrow" style={{ color: "#5790C9" }}>MULTI-DISCIPLINE 3D</div>
        <h2 className="grantha-problem-h reveal">A complete facility model. Every discipline. Every clash.</h2>
        <div className="rc-disc-grid reveal">
          {DISCIPLINES.map((d, i) => {
            const Icon = d.icon;
            return (
              <div key={i} className="rc-disc" data-testid={`rc-disc-${i}`}>
                <Icon size={28} className="rc-disc-icon" />
                <div className="rc-disc-name">{d.name}</div>
                <div className="rc-disc-detail">{d.detail}</div>
              </div>
            );
          })}
        </div>
        <p className="grantha-problem-quote reveal">
          Every discipline. One model. IFC 4.3 / STEP AP242 / glTF 2.0 / DWG output.
        </p>
      </div>
    </section>
  );
}

/* ═══════ S7 DELIVERABLES ═══════ */
function SectionDeliverables() {
  return (
    <section className="grantha-problem" data-testid="rc-deliverables">
      <div className="container">
        <div className="eyebrow" style={{ color: "#5790C9" }}>BEYOND DRAWINGS</div>
        <h2 className="grantha-problem-h reveal">A complete engineering deliverable package.</h2>
        <p className="reveal" style={{ maxWidth: 680, marginBottom: 36, color: "var(--text-2)", lineHeight: 1.6 }}>
          Formigen doesn&rsquo;t just generate drawings. It generates the full engineering deliverable package that a FEED or detailed design engagement traditionally produces.
        </p>
        <div className="rc-deliv-grid reveal">
          {DELIVERABLES.map((d, i) => (
            <div key={i} className="rc-deliv" data-testid={`rc-deliv-${i}`}>
              <div className="rc-deliv-name">{d.name}</div>
              <div className="rc-deliv-format">{d.format}</div>
              <div className="rc-deliv-detail">{d.detail}</div>
            </div>
          ))}
        </div>
        <p className="grantha-manifesto reveal" style={{ fontStyle: "italic" }}>
          &ldquo;One engine. Eight deliverables. Zero integration headaches.&rdquo;
        </p>
      </div>
    </section>
  );
}

/* ═══════ S8 COMPARISON ═══════ */
function SectionComparison() {
  return (
    <section className="grantha-problem" data-testid="rc-comparison">
      <div className="container">
        <div className="eyebrow" style={{ color: "#5790C9" }}>WHY FORMIGEN</div>
        <h2 className="grantha-problem-h reveal">Three kinds of engineering design tools exist. None are generative.</h2>
        <div className="rc-compare reveal">
          <div className="rc-compare-col">
            <div className="rc-compare-head">Legacy CAD + 3D</div>
            <div className="rc-compare-sub">incumbent 3D plant-design suites</div>
            <ul>
              <li>Drawing/modeling authorship</li>
              <li>6-18 months to proficiency</li>
              <li>Engineer-hours per deliverable</li>
              <li>Tool doesn&rsquo;t reason</li>
              <li>{"\u20b9"}4-40L per seat</li>
            </ul>
          </div>
          <div className="rc-compare-col">
            <div className="rc-compare-head">Modern Simulation</div>
            <div className="rc-compare-sub">process simulators</div>
            <ul>
              <li>Process simulation & optimization</li>
              <li>3-9 months to proficiency</li>
              <li>Simulation-hours per scenario</li>
              <li>No P&ID or 3D output</li>
              <li>{"\u20b9"}6-30L per seat</li>
            </ul>
          </div>
          <div className="rc-compare-col rc-compare-highlight">
            <div className="rc-compare-head">Formigen (Capexara)</div>
            <div className="rc-compare-sub">Generative AI Engineering Engine</div>
            <ul>
              <li>Generative design from natural language</li>
              <li>30 minutes to first output</li>
              <li>Hours per facility (not weeks)</li>
              <li>4,173-rule validation engine</li>
              <li>{"\u20b9"}25K-2L per design engagement</li>
            </ul>
          </div>
        </div>
        <p className="grantha-problem-quote reveal">
          &ldquo;Existing tools were built to draft what you already decided. Formigen was built to decide what to draft.&rdquo;
        </p>
      </div>
    </section>
  );
}

/* ═══════ S9 FAQ ═══════ */
function SectionFaq() {
  return (
    <section className="grantha-problem" data-testid="rc-faq">
      <div className="container">
        <div className="eyebrow" style={{ color: "#5790C9" }}>FREQUENTLY ASKED</div>
        <h2 className="grantha-problem-h reveal">Everything an engineering team wants to know.</h2>
        <div className="grantha-faq-list">
          {FAQS.map((f, i) => <FaqItem key={i} {...f} />)}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S10 CTA ═══════ */
function SectionCta() {
  return (
    <section className="grantha-cta-band" data-testid="rc-cta">
      <div className="container">
        <div className="grantha-cta-grid">
          <div>
            <div className="eyebrow" style={{ color: "#F26B1F" }}>SEE FORMIGEN DESIGN YOUR NEXT PLANT</div>
            <h2 className="grantha-cta-h2">
              Bring us your hardest design scope. We&rsquo;ll generate the PFD, the P&ID, and the 3D model &mdash; in days.
            </h2>
            <p style={{ color: "#C2D5EE", maxWidth: 560, lineHeight: 1.6 }}>
              Our &ldquo;Prompt-to-Plant Demo&rdquo; is a working engagement where we take a scope document from your real project (under NDA), generate the full design package, and walk your engineering team through every decision.
            </p>
          </div>
          <CtaForm source="rachana" />
        </div>
      </div>
    </section>
  );
}
